import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Clock, CheckCircle, Zap, Users, Shield, X, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

const HoursLanding = () => {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <Helmet>
        <title>Build Your Project Team Today — Not Next Month | HireX8</title>
        <meta name="description" content="Pre-vetted, AI-matched talent ready to execute before the week is out. Zero platform fees. Built for digital agencies." />
        <meta property="og:title" content="Build Your Project Team Today — Not Next Month | HireX8" />
        <meta property="og:description" content="Pre-vetted, AI-matched talent ready to execute before the week is out. Zero platform fees." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-orange-500">
        {/* Background Image */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(/hours-hero.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />

        {/* Gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-orange-600/95 via-orange-500/80 to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full py-20">
          <div className="flex flex-col items-start text-left space-y-5 max-w-2xl">
            {/* Logo */}
            <Link to="/" className="mb-2">
              <span className="text-2xl font-bold text-white">HireX8™</span>
            </Link>

            {/* Badge */}
            <div className="flex items-center gap-2 text-sm">
              <div className="w-2 h-2 rounded-full bg-white"></div>
              <span className="font-medium text-white/90">Built for Digital Agencies</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight">
              Build Your Project Team <span className="text-black">Today.</span> Not Next Month.
            </h1>

            {/* Subheadline */}
            <div className="space-y-1">
              <p className="text-xl text-white/90">
                Your client said yes. Now you need a team — fast.
              </p>
              <p className="text-xl text-white/90">
                Pre-vetted, AI-matched talent ready to execute before the week is out.
              </p>
            </div>

            {/* Feature bullets */}
            <div className="grid sm:grid-cols-3 gap-4 py-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <h3 className="text-white font-bold mb-1">Pre-vetted. No interviews.</h3>
                <p className="text-white/80 text-sm">Every operator is screened before they reach you.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <h3 className="text-white font-bold mb-1">AI-matched in hours.</h3>
                <p className="text-white/80 text-sm">Our AI scores fit, past performance, and project match.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <h3 className="text-white font-bold mb-1">Zero platform fees.</h3>
                <p className="text-white/80 text-sm">No contracts. No minimums. Scale up or down per project.</p>
              </div>
            </div>

            {/* CTA */}
            <div className="flex flex-col items-start gap-3">
              <a
                href="/meet-with-an-expert"
                className="bg-black hover:bg-gray-900 text-white text-lg font-bold px-8 py-4 rounded-lg inline-flex items-center gap-3 transition-all shadow-lg"
              >
                Talk to an Expert
                <ArrowRight className="w-5 h-5" />
              </a>
              <p className="text-white/70 text-sm">
                Free 20-minute call. No commitment required.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-orange-500"></div>
              <span className="text-sm text-gray-600 font-medium uppercase tracking-wider">How It Works</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">
              From Brief to <span className="text-orange-500">Active Team in Hours</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mt-4">
              Tell us what you need. We handle the rest. Your team is ready before your next client call.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: '1', title: 'Tell Us What You Need', desc: 'Describe your project, skills needed, timeline, and budget. Takes 5 minutes.' },
              { step: '2', title: 'Get Matched Instantly', desc: 'Our AI scores every candidate on skill fit, past performance, and project similarity.' },
              { step: '3', title: 'Start Working Today', desc: 'Every match is reviewed by our team before it\'s presented to you. No unpleasant surprises.' },
              { step: '4', title: 'Deliver Excellence', desc: 'Your team hits the ground running. AI-assisted workflows mean better output, faster delivery.' },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-14 h-14 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold text-white">{item.step}</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Talent Types Section */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">
              Access <span className="text-orange-500">Every Skill</span> You Need
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mt-4">
              Scale into new services without adding headcount. We cover everything from design to AI.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Design */}
            <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all">
              <div className="text-4xl mb-4">🎨</div>
              <h3 className="text-2xl font-bold text-orange-500 mb-4">Design</h3>
              <ul className="space-y-2 text-gray-600">
                {['UI/UX Design', 'Brand Identity', 'Marketing Graphics', 'Motion Design'].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-orange-500" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Engineering */}
            <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all">
              <div className="text-4xl mb-4">💻</div>
              <h3 className="text-2xl font-bold text-orange-500 mb-4">Engineering</h3>
              <ul className="space-y-2 text-gray-600">
                {['Frontend Development', 'Backend Development', 'Mobile Apps', 'AI Integration'].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-orange-500" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Copy */}
            <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all">
              <div className="text-4xl mb-4">✍️</div>
              <h3 className="text-2xl font-bold text-orange-500 mb-4">Copy</h3>
              <ul className="space-y-2 text-gray-600">
                {['Website Copy', 'Blog & SEO Content', 'Ad Copy', 'Email Campaigns'].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-orange-500" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* AI & Automation */}
            <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all">
              <div className="text-4xl mb-4">🤖</div>
              <h3 className="text-2xl font-bold text-orange-500 mb-4">AI & Automation</h3>
              <ul className="space-y-2 text-gray-600">
                {['AI Implementation', 'Prompt Engineering', 'Workflow Automation', 'AI-Assisted Production'].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-orange-500" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why Agencies Switch Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">
              Why Agencies Switch From <span className="text-orange-500">Upwork</span> to HireX8
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* The bad side */}
            <div className="bg-white rounded-2xl p-8 border border-red-200">
              <div className="flex items-center gap-2 mb-6">
                <Clock className="w-6 h-6 text-red-500" />
                <h3 className="text-xl font-bold text-red-500">Upwork / Freelancer Marketplaces</h3>
              </div>
              <ul className="space-y-4">
                {[
                  '2–4 weeks to find and vet the right person',
                  '15–20% platform fee on every dollar you spend',
                  'You do all the screening — and still gamble on fit',
                  'No delivery guarantee. Miss a deadline? Your problem.',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-600">
                    <X className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* HireX8 Way */}
            <div className="bg-orange-50 rounded-2xl p-8 border border-orange-200">
              <div className="flex items-center gap-2 mb-6">
                <Zap className="w-6 h-6 text-orange-500" />
                <h3 className="text-xl font-bold text-orange-500">HireX8</h3>
              </div>
              <ul className="space-y-4">
                {[
                  'Matched in hours — team ready same week',
                  'Zero platform fees — your full budget goes to work',
                  'AI-screened and human-verified before they reach you',
                  'Delivery guarantee — free replacement if needed',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-700">
                    <CheckCircle className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="text-center mt-12">
            <a
              href="https://vibe.hirex8.com/register?type=client"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black hover:bg-gray-900 text-white text-lg font-bold px-8 py-4 rounded-lg inline-flex items-center gap-3 transition-all"
            >
              Talk to an Expert — It's Free
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <div className="text-5xl sm:text-6xl font-bold text-orange-500 mb-2">8x</div>
              <p className="text-gray-600">Faster than traditional hiring or freelancer search</p>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <div className="text-5xl sm:text-6xl font-bold text-orange-500 mb-2">0%</div>
              <p className="text-gray-600">Platform fees — your full budget goes to execution</p>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <div className="text-5xl sm:text-6xl font-bold text-orange-500 mb-2">Hours</div>
              <p className="text-gray-600">To match with pre-vetted talent</p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-orange-500"></div>
              <span className="text-sm text-gray-600 font-medium uppercase tracking-wider">Agency Wins</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">
              Agencies that stopped waiting.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-orange-500 text-orange-500" />
                ))}
              </div>
              <p className="text-gray-700 mb-6">"We had a $40K project land with a two-week start date. HireX8 had us a full-stack dev and a designer within 48 hours. We didn't lose the client."</p>
              <div>
                <p className="font-bold text-gray-900">Marcus R.</p>
                <p className="text-sm text-gray-500">Founder, 12-person digital agency · New York</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-orange-500 text-orange-500" />
                ))}
              </div>
              <p className="text-gray-700 mb-6">"We were spending $3–4K a month in Upwork fees alone. HireX8 cut that to zero overnight. Our margin went from 28% to 41%."</p>
              <div>
                <p className="font-bold text-gray-900">Sophia P.</p>
                <p className="text-sm text-gray-500">Operations Lead, growth agency · Austin</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-orange-500 text-orange-500" />
                ))}
              </div>
              <p className="text-gray-700 mb-6">"I was turning down AI work because I didn't have the people. HireX8 got me two AI automation specialists in a week. We've taken on four new retainers since."</p>
              <div>
                <p className="font-bold text-gray-900">Daniel J.</p>
                <p className="text-sm text-gray-500">CEO, 18-person agency · Toronto</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 px-6 bg-orange-500">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Your next project doesn't have to wait.
          </h2>

          <p className="text-xl text-white/90 mb-10">
            Talk to one of our agency specialists. We'll map out exactly what talent you need, when you need it — and what it'll cost. No platform fees. No surprises.
          </p>

          <a
            href="https://vibe.hirex8.com/register?type=client"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-black hover:bg-gray-900 text-white text-xl font-bold px-12 py-5 rounded-lg shadow-lg transition-all hover:scale-105"
          >
            Book a Free 20-Min Call
            <ArrowRight className="w-6 h-6" />
          </a>

          <p className="mt-6 text-white/70 text-sm">
            No commitment. No sales pressure. Just straight answers.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-gray-900">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="text-2xl font-bold text-white">
            HireX8™
          </Link>
          <div className="flex items-center gap-6 text-gray-400 text-sm">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <span>© 2026 HireX8. All rights reserved.</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HoursLanding;
